import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { isNativePrintAvailable, printDocketsNative } from '../../lib/nativePrint';

function isTracked(n) {
  return n !== null && n !== undefined && n !== '';
}

function moneyExtra(n) {
  const v = Number(n) || 0;
  if (v <= 0) return '';
  return ` +$${v.toFixed(2)}`;
}

function formatOptions(raw) {
  if (!raw) return '';
  let obj = raw;
  if (typeof raw === 'string') {
    try { obj = JSON.parse(raw); } catch { return raw; }
  }
  if (typeof obj !== 'object' || Array.isArray(obj)) return '';
  return Object.entries(obj).filter(([, v]) => v).map(([, v]) => v).join(', ');
}

function formatOrderTime(raw) {
  if (!raw) return '';
  const d = new Date(String(raw).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(raw).slice(11, 16);
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function statusLabel(status) {
  if (status === 'collected') return 'Done';
  if (status === 'ready') return 'Ready';
  if (status === 'preparing') return 'Kitchen';
  if (status === 'pending') return 'Open';
  return status || '';
}

function isKitchenOrder(order) {
  return order?.venue !== 'cafe' && order?.venue !== 'counter';
}

function groupOrdersForDisplay(orders) {
  return (orders || []).map((order) => {
    const kitchenOrders = isKitchenOrder(order) ? [order] : [];
    const cafeOrders = order.venue === 'cafe' ? [order] : [];
    return {
      id: order.id,
      key: `order-${order.id}`,
      order_number: order.order_number,
      status: statusLabel(order.status),
      created_at: order.created_at,
      customer_name: order.customer_name || '',
      channel: order.channel,
      station_name: order.station_name,
      total: Number(order.total || 0),
      items: order.items || [],
      kitchenOrders,
      cafeOrders,
      members: [order],
    };
  });
}

function StockStepper({ value, disabled, onBump }) {
  if (!isTracked(value)) {
    return <span className="pos-menu-unlimited">Unlimited</span>;
  }
  return (
    <div className="pos-menu-stepper">
      <button
        type="button"
        className="pos-cart-qty-btn"
        disabled={disabled || Number(value) <= 0}
        onClick={() => onBump(-1)}
        aria-label="Decrease stock"
      >
        &minus;
      </button>
      <span className="pos-menu-stepper-value">{Number(value)}</span>
      <button
        type="button"
        className="pos-cart-qty-btn"
        disabled={disabled}
        onClick={() => onBump(1)}
        aria-label="Increase stock"
      >
        +
      </button>
    </div>
  );
}

export default function PosMenuDrawer({ open, onClose, onMenuChanged }) {
  const [tab, setTab] = useState('board');
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [optionName, setOptionName] = useState('');
  const [optionExtra, setOptionExtra] = useState('');
  const [orders, setOrders] = useState([]);
  const [reprintingId, setReprintingId] = useState(null);

  const loadBoard = useCallback(async () => {
    const rows = await api.getPosMenuManage();
    setItems(Array.isArray(rows) ? rows : []);
  }, []);

  const loadGroups = useCallback(async () => {
    const rows = await api.getPosModifierGroups();
    setGroups(Array.isArray(rows) ? rows : []);
  }, []);

  const loadOrders = useCallback(async () => {
    const rows = await api.getPosTodayOrders();
    setOrders(Array.isArray(rows) ? rows : []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'mods') await loadGroups();
      else if (tab === 'orders') await loadOrders();
      else await loadBoard();
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab, loadBoard, loadGroups, loadOrders]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return undefined;
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      setTab('board');
      setOpenGroupId(null);
      setSearch('');
      setOptionName('');
      setOptionExtra('');
      setError('');
      setOrders([]);
    }
  }, [open]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? items.filter((it) => (
        it.name.toLowerCase().includes(q)
        || String(it.category_name || '').toLowerCase().includes(q)
      ))
      : items.slice();
    rows.sort((a, b) => {
      const aOn = Number(a.active) === 1 ? 0 : 1;
      const bOn = Number(b.active) === 1 ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return String(a.name).localeCompare(String(b.name));
    });
    return rows;
  }, [items, search]);

  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? groups.filter((g) => g.name.toLowerCase().includes(q))
      : groups.slice();
    return rows;
  }, [groups, search]);

  const visibleOrders = useMemo(() => {
    const grouped = groupOrdersForDisplay(orders);
    const q = search.trim().toLowerCase();
    if (!q) return grouped;
    return grouped.filter((sale) => (
      sale.members.some((o) => (
        String(o.order_number || '').toLowerCase().includes(q)
        || String(o.customer_name || '').toLowerCase().includes(q)
      ))
    ));
  }, [orders, search]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === openGroupId) || null,
    [groups, openGroupId]
  );

  const run = async (key, fn, apply) => {
    if (busyKey) return;
    setBusyKey(key);
    setError('');
    try {
      const result = await fn();
      setItems((prev) => apply(prev, result));
      onMenuChanged?.();
    } catch (err) {
      setError(err.message || 'Update failed');
    } finally {
      setBusyKey('');
    }
  };

  const toggleActive = (item) => {
    const nextOn = Number(item.active) !== 1;
    run(`item-on-${item.id}`, () => api.setPosItemActive(item.id, nextOn), (prev) => (
      prev.map((row) => (row.id === item.id ? { ...row, active: nextOn ? 1 : 0 } : row))
    ));
  };

  const bumpItem = (item, delta) => {
    run(`item-qty-${item.id}`, () => api.bumpPosItemStock(item.id, delta), (prev, result) => (
      prev.map((row) => (
        row.id === item.id ? { ...row, stock_quantity: result.stock_quantity } : row
      ))
    ));
  };

  const bumpOption = (item, option, delta) => {
    run(`opt-qty-${option.id}`, () => api.bumpPosOptionStock(option.id, delta), (prev, result) => (
      prev.map((row) => {
        if (!row.stock_options?.some((o) => o.id === option.id)) return row;
        return {
          ...row,
          stock_options: row.stock_options.map((o) => (
            o.id === option.id ? { ...o, stock_quantity: result.stock_quantity } : o
          )),
        };
      })
    ));
  };

  const addOption = async (e) => {
    e?.preventDefault?.();
    if (!selectedGroup || busyKey) return;
    const name = optionName.replace(/\s+/g, ' ').trim();
    if (!name) {
      setError('Enter an option name');
      return;
    }
    const extra = optionExtra.trim();
    const extraLabel = extra && Number(extra) > 0 ? ` (+$${Number(extra).toFixed(2)})` : '';
    if (!window.confirm(`Add “${name}”${extraLabel} to ${selectedGroup.name}?\n\nIt will show on the POS and the customer app.`)) {
      return;
    }
    setBusyKey('add-opt');
    setError('');
    try {
      const result = await api.addPosModifierOption(selectedGroup.id, {
        name,
        surcharge: extra === '' ? 0 : extra,
      });
      setGroups((prev) => prev.map((g) => {
        if (g.id !== selectedGroup.id) return g;
        return { ...g, options: [...(g.options || []), result.option] };
      }));
      setOptionName('');
      setOptionExtra('');
      onMenuChanged?.();
    } catch (err) {
      setError(err.message || 'Failed to add option');
    } finally {
      setBusyKey('');
    }
  };

  const removeOption = async (opt) => {
    if (!selectedGroup || !opt?.id || busyKey) return;
    if (!window.confirm(
      `Remove “${opt.name}” from ${selectedGroup.name}?\n\nIt will disappear from the POS and the customer app.`
    )) {
      return;
    }
    setBusyKey(`del-opt-${opt.id}`);
    setError('');
    try {
      await api.deletePosModifierOption(opt.id);
      setGroups((prev) => prev.map((g) => {
        if (g.id !== selectedGroup.id) return g;
        return { ...g, options: (g.options || []).filter((o) => o.id !== opt.id) };
      }));
      onMenuChanged?.();
    } catch (err) {
      setError(err.message || 'Failed to remove option');
    } finally {
      setBusyKey('');
    }
  };

  const reprintSale = async (sale) => {
    if (!sale?.id || reprintingId) return;
    setReprintingId(sale.key);
    setError('');
    try {
      const clientPrints = isNativePrintAvailable();
      const kitchen = sale.kitchenOrders[0];
      const cafe = sale.cafeOrders[0];
      if (kitchen) {
        const result = await api.printKitchenDocket({
          orderId: kitchen.id,
          clientPrints,
        });
        if (clientPrints && result.dockets?.length) {
          const printed = await printDocketsNative(result.dockets);
          if (printed.failed?.length) {
            throw new Error(printed.failed.map((f) => f.error).join(', '));
          }
        }
      } else if (cafe) {
        await api.reprintCafeOrderStickers(cafe.id);
      } else {
        throw new Error('Nothing to reprint for this order');
      }
    } catch (err) {
      setError(err.message || 'Reprint failed');
    } finally {
      setReprintingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="pos-menu-drawer-backdrop" onClick={onClose} role="presentation">
      <aside
        className="pos-menu-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Menu board"
      >
        <div className="pos-menu-drawer-head">
          <div>
            <h2>{tab === 'mods' ? 'Modifiers' : tab === 'orders' ? "Today's orders" : 'Menu board'}</h2>
            <p>
              {tab === 'mods'
                ? 'Add or remove a choice on an existing group. It shows on the POS and the app.'
                : tab === 'orders'
                  ? 'This service day. Reprint prints only the ticket on that row.'
                  : 'Turn items on or off and adjust stock. Names and prices stay in Admin.'}
            </p>
          </div>
          <button type="button" className="pos-menu-drawer-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="pos-menu-drawer-tabs">
          <button
            type="button"
            className={`pos-menu-drawer-tab${tab === 'board' ? ' is-on' : ''}`}
            onClick={() => {
              setTab('board');
              setOpenGroupId(null);
              setSearch('');
              setError('');
            }}
          >
            Menu board
          </button>
          <button
            type="button"
            className={`pos-menu-drawer-tab${tab === 'mods' ? ' is-on' : ''}`}
            onClick={() => {
              setTab('mods');
              setSearch('');
              setError('');
            }}
          >
            Modifiers
          </button>
          <button
            type="button"
            className={`pos-menu-drawer-tab${tab === 'orders' ? ' is-on' : ''}`}
            onClick={() => {
              setTab('orders');
              setOpenGroupId(null);
              setSearch('');
              setError('');
            }}
          >
            Orders
          </button>
        </div>

        {tab === 'mods' && selectedGroup ? (
          <button
            type="button"
            className="pos-menu-drawer-back"
            onClick={() => {
              setOpenGroupId(null);
              setOptionName('');
              setOptionExtra('');
              setError('');
            }}
          >
            ← All groups
          </button>
        ) : (
          <input
            type="search"
            className="pos-menu-drawer-search"
            placeholder={
              tab === 'mods'
                ? 'Search groups…'
                : tab === 'orders'
                  ? 'Search B12 or name…'
                  : 'Search items…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}

        {error && <div className="pos-menu-drawer-error">{error}</div>}
        {loading && <div className="pos-menu-drawer-hint">Loading…</div>}

        {tab === 'board' && (
          <div className="pos-menu-drawer-list">
            {visible.map((item) => {
              const on = Number(item.active) === 1;
              const optionStock = Array.isArray(item.stock_options) ? item.stock_options : [];
              const itemBusy = busyKey === `item-on-${item.id}` || busyKey === `item-qty-${item.id}`;
              return (
                <div key={item.id} className={`pos-menu-row${on ? '' : ' is-off'}`}>
                  <div className="pos-menu-row-main">
                    <div className="pos-menu-row-copy">
                      <div className="pos-menu-row-name">{item.name}</div>
                      <div className="pos-menu-row-meta">{item.category_name || '—'}</div>
                    </div>
                    <button
                      type="button"
                      className={`pos-menu-onoff${on ? ' is-on' : ''}`}
                      disabled={!!busyKey}
                      onClick={() => toggleActive(item)}
                    >
                      {itemBusy && busyKey === `item-on-${item.id}` ? '…' : on ? 'On' : 'Off'}
                    </button>
                    {optionStock.length === 0 && (
                      <StockStepper
                        value={item.stock_quantity}
                        disabled={!!busyKey}
                        onBump={(d) => bumpItem(item, d)}
                      />
                    )}
                  </div>
                  {optionStock.length > 0 && (
                    <div className="pos-menu-options">
                      {optionStock.map((opt) => (
                        <div key={opt.id} className="pos-menu-option">
                          <span className="pos-menu-option-name">
                            {opt.group_name}: {opt.name}
                          </span>
                          <StockStepper
                            value={opt.stock_quantity}
                            disabled={!!busyKey}
                            onBump={(d) => bumpOption(item, opt, d)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {!loading && visible.length === 0 && (
              <div className="pos-menu-drawer-hint">No items match.</div>
            )}
          </div>
        )}

        {tab === 'orders' && (
          <div className="pos-menu-drawer-list">
            {visibleOrders.map((sale) => {
              const items = Array.isArray(sale.items) ? sale.items : [];
              const reprinting = reprintingId === sale.key;
              return (
                <div key={sale.key} className="pos-menu-row pos-order-row">
                  <div className="pos-order-top">
                    <div className="pos-menu-row-copy">
                      <div className="pos-menu-row-name">
                        {sale.order_number}
                        <span className="pos-order-status">{sale.status}</span>
                      </div>
                      <div className="pos-menu-row-meta">
                        {formatOrderTime(sale.created_at)}
                        {sale.customer_name ? ` · ${sale.customer_name}` : ''}
                        {sale.channel === 'online' ? ' · App' : sale.station_name ? ` · ${sale.station_name}` : ''}
                        {` · $${Number(sale.total || 0).toFixed(2)}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="pos-order-reprint"
                      disabled={!!reprintingId}
                      onClick={() => reprintSale(sale)}
                    >
                      {reprinting ? '…' : 'Reprint'}
                    </button>
                  </div>
                  <div className="pos-order-items">
                    {items.map((it, idx) => {
                      const extras = formatOptions(it.options);
                      const note = it.item_note ? ` — ${it.item_note}` : '';
                      return (
                        <div key={`${sale.key}-${idx}`}>
                          {sale.members.length > 1 ? `${it.order_number} · ` : ''}
                          {it.quantity}× {it.item_name}
                          {extras ? ` (${extras})` : ''}
                          {note}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {!loading && visibleOrders.length === 0 && (
              <div className="pos-menu-drawer-hint">
                {search.trim() ? 'No orders match.' : 'No orders this service day yet.'}
              </div>
            )}
          </div>
        )}

        {tab === 'mods' && !selectedGroup && (
          <div className="pos-menu-drawer-list">
            {visibleGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                className="pos-menu-row pos-menu-group-btn"
                onClick={() => {
                  setOpenGroupId(group.id);
                  setSearch('');
                  setError('');
                }}
              >
                <div className="pos-menu-row-copy">
                  <div className="pos-menu-row-name">{group.name}</div>
                  <div className="pos-menu-row-meta">
                    {(group.options || []).length} choice{(group.options || []).length === 1 ? '' : 's'}
                  </div>
                </div>
                <span className="pos-menu-group-chevron" aria-hidden>›</span>
              </button>
            ))}
            {!loading && visibleGroups.length === 0 && (
              <div className="pos-menu-drawer-hint">No modifier groups yet. Add them in Admin.</div>
            )}
          </div>
        )}

        {tab === 'mods' && selectedGroup && (
          <div className="pos-menu-drawer-list">
            <div className="pos-menu-row">
              <div className="pos-menu-row-name">{selectedGroup.name}</div>
              <div className="pos-menu-options">
                {(selectedGroup.options || []).map((opt) => (
                  <div key={opt.id} className="pos-menu-option">
                    <span className="pos-menu-option-name">
                      {opt.name}{moneyExtra(opt.surcharge)}
                    </span>
                    <button
                      type="button"
                      className="pos-menu-option-remove"
                      disabled={!!busyKey}
                      onClick={() => removeOption(opt)}
                    >
                      {busyKey === `del-opt-${opt.id}` ? '…' : 'Remove'}
                    </button>
                  </div>
                ))}
                {(selectedGroup.options || []).length === 0 && (
                  <div className="pos-menu-drawer-hint">No choices yet.</div>
                )}
              </div>
            </div>

            <form className="pos-menu-add-option" onSubmit={addOption}>
              <label className="pos-menu-add-label" htmlFor="pos-new-option-name">
                New choice
              </label>
              <input
                id="pos-new-option-name"
                className="pos-menu-drawer-search"
                type="text"
                maxLength={40}
                value={optionName}
                onChange={(e) => setOptionName(e.target.value)}
                placeholder="e.g. Lactose free"
                autoComplete="off"
              />
              <label className="pos-menu-add-label" htmlFor="pos-new-option-extra">
                Extra $ (optional)
              </label>
              <input
                id="pos-new-option-extra"
                className="pos-menu-drawer-search"
                type="number"
                inputMode="decimal"
                min="0"
                max="99.99"
                step="0.01"
                value={optionExtra}
                onChange={(e) => setOptionExtra(e.target.value)}
                placeholder="0.00"
              />
              <button
                type="submit"
                className="pos-menu-add-btn"
                disabled={!!busyKey || !optionName.trim()}
              >
                {busyKey === 'add-opt' ? 'Adding…' : `Add to ${selectedGroup.name}`}
              </button>
            </form>
          </div>
        )}
      </aside>
    </div>
  );
}
